(ns common.widgets.resize
  (:require ["@mui/material/Box$default" :as Box]
            [common.util :refer [spread-obj]]
            [uix.core :as uix :refer [$ defhook defui]]))

;; If large width, like 1000px, is saved to the localStorage,
;; the width is limited according to the layout.
;; Resizing the window might allow the panel to grow larger,
;; if the width was larger than the layout allowed.
;; When ever panel is resized, the latest value is saved.

(def ACTIVATE-AREA-WIDTH 15)
(def BORDER-WIDTH 3)

(defn clamp-width
  "Limit the width between min and max values.

  Max value is rarely used because the page layout will limit the panel width
  in practice."
  [v min-width]
  (max v (min v 5000) min-width))

(defn right-side-width
  "Calculate width from cursor position to the right corner of the viewport"
  [cursor-x delta]
  (- (.-innerWidth js/window) cursor-x delta))

(defn left-side-width
  "Cursor position for left side panel. The width is just same as the cursor x coordinate."
  [cursor-x delta]
  (- cursor-x delta))

(defui resize-handle [{:keys [width set-width calculate-width ^js sx ^js border-sx
                              on-done]}]
  (let [[{:keys [active delta]} set-resize-state] (uix/use-state {:active false})
        move-callback (uix/use-callback (fn [e]
                                          (set-width (calculate-width (.-clientX e) delta)))
                                        ;; set-width is stable
                                        ^:lint/disable
                                        [delta calculate-width])
        up-callback (uix/use-callback (fn [e]
                                        (set-resize-state {:active false})
                                        (when on-done
                                          (on-done (calculate-width (.-clientX e) delta))))
                                      [delta on-done calculate-width])]
    ;; Move and up handlers need to be attached to the window,
    ;; as registering them to handle would only listen for events
    ;; when the cursor is exactly over the handle.
    ;; react-use/useEvent doesn't allow active toggle so using use-effect.
    (uix/use-effect (fn []
                      (if active
                        (do
                          (.addEventListener js/window "mousemove" move-callback)
                          (.addEventListener js/window "mouseup" up-callback)
                          (fn []
                            (.removeEventListener js/window "mousemove" move-callback)
                            (.removeEventListener js/window "mouseup" up-callback)))
                        js/undefined))
                    [active move-callback up-callback])
    ;; 15px area which activates the resize.
    ;; TODO: Positioning for left side panel?
    ;; Now the activate area & border are positioned on the panel side of the border,
    ;; because the current layout doesn't leave any space between panel border and the table.
    ;; Later the design might have margin between panel and content/table so that we can
    ;; display the active area and border on the middle of the margin.
    ($ :<>
       ($ Box
          {:sx (fn [^js theme]
                 (spread-obj #js {:position "absolute"
                                  :top 0
                                  :bottom 0
                                  :width ACTIVATE-AREA-WIDTH
                                  :cursor "ew-resize"
                                  ;; Over regular stuff, but below toggle button (6)
                                  :zIndex 5
                                  "&:hover" #js {"--borderColor" (.. theme -palette -primary -main)}}
                             sx))
           :onMouseDown (fn [e]
                          ;; Prevent text selection when mouse drag started from resize area
                          (.preventDefault e)
                          (let [;; Find position of mouse vs. the resize handle.
                                delta (calculate-width (.-clientX e) width)]
                            (set-resize-state {:active true
                                               :delta delta})))}
          ;; Smaller border which shows the visual border for hover and active status.
          ($ Box
             {:sx (spread-obj #js {:position "absolute"
                                   :top 0
                                   :bottom 0
                                   :width BORDER-WIDTH
                                   ;; Keep the color when resize is active, even if not hovering anymore.
                                   ;; When going over the limits, state is kept but cursor wont hover the anchor anymore.
                                   :backgroundColor (if active
                                                      "primary.main"
                                                      "var(--borderColor)")
                                   :opacity 0.7}
                              border-sx)}))
       ;; Overlay to prevent drawer contents (think of iframes) from capturing mouse events while resizing.
       ;; Without the overlay, when the cursor moves over drawer contens, it can intercept mouse events (like :mousemove and :mouseup),
       ;; causing the resize handler to stop responding.
       ;; Adding a transparent overlay over the iframe while resizing ensures that mouse events are captured by the resize logic
       ;; and not blocked by the iframe. Once the resize ends, the overlay is removed.
       (when active
         ($ Box
            {:sx #js {:position "absolute"
                      :inset 0}})))))

(defhook use-drawer-width
  "Sets up local state for the drawer width, and stores the changes
  to localStorage with given key."
  [{:keys [k min-width default-width]
    :or {min-width 300
         default-width 300}}]
  ;; react-use/lib/useLocalStorage saves the value
  ;; to localStorage before state hook, so that might
  ;; be a bit slower than setting state hook directly and then
  ;; copying value to localStorage in an effect.
  (let [[width* set-width] (uix/use-state (fn read-initial-width []
                                            (try
                                              (some-> (js/localStorage.getItem k)
                                                      js/parseInt)
                                              (catch :default _e
                                                nil))))
        width (clamp-width (or width* default-width) min-width)]
    (uix/use-effect (fn []
                      (js/localStorage.setItem k width))
                    [k width])
    [width set-width]))
